/* eslint-disable sonarjs/no-clear-text-protocols */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { ValidatedTextInput } from '@els/els-react--text-input';
import { Banner } from '@els/els-react--banner';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { LinkElement } from '@els/els-react--link-element';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { paths } from '../../../../AppRoutes/constants';
import LoginFormHeader from '../../Components/LoginFormHeader';

import './BulkIdRegisterForm.scss';
import '../../Components/LoginForm.scss';

function BulkIdRegisterForm({ ipAddress, loginFail, isLoading, submit, data, setData, clearLoginFail }) {
  const spinnerId = 'bulkIdRegisterForm';
  const [showPassword, setShowPassword] = useState(false);
  const [touchedPassword, setTouchedPassword] = useState(false);
  const [touchedId, setTouchedId] = useState(false);
  const [isInfoBannerVisible, setIsInfoBannerVisible] = useState(true);
  const [validationResetKey, setValidationResetKey] = useState(0);
  const history = useHistory();
  const bulkIdRef = useRef(null);
  const bulkPasswordRef = useRef(null);

  const hasBulkId = Boolean(data?.bulkId?.trim());
  const hasBulkPassword = Boolean(data?.bulkPassword?.trim());
  const bulkIdRequiredMessage = 'Registration ID is required.';
  const bulkPasswordRequiredMessage = 'Password is required.';

  useEffect(() => {
    if (loginFail) {
      setTouchedId(false);
      setTouchedPassword(false);
      setValidationResetKey((key) => key + 1);
    }
  }, [loginFail]);

  const bulkIdValidator = () => {
    if (hasBulkId) {
      return null;
    }
    return [{ isValid: false, message: bulkIdRequiredMessage }];
  };

  const bulkPasswordValidator = () => {
    if (hasBulkPassword) {
      return null;
    }
    return [{ isValid: false, message: bulkPasswordRequiredMessage }];
  };

  const removeLoginFailError = () => {
    if (!loginFail) {
      return;
    }
    clearLoginFail();
  };

  const submitHandler = () => {
    if (!hasBulkId || !hasBulkPassword) {
      removeLoginFailError();
      bulkIdRef.current?.validate(data.bulkId, true);
      bulkPasswordRef.current?.validate(data.bulkPassword, true);
      return;
    }

    submit();
  };

  const touchBulkId = () => {
    if (loginFail) {
      removeLoginFailError();
    }
    if (touchedId) {
      return;
    }
    setTouchedId(true);
  };

  const touchPassword = () => {
    if (loginFail) {
      removeLoginFailError();
    }
    if (touchedPassword) {
      return;
    }
    setTouchedPassword(true);
  };

  const changeBulkId = (value) => {
    setData({
      ...data,
      bulkId: value,
    });
    if (loginFail) {
      removeLoginFailError();
    }
  };

  const changeBulkPassword = (value) => {
    setData({
      ...data,
      bulkPassword: value,
    });
    if (loginFail) {
      removeLoginFailError();
    }
  };

  const togglePassword = (e) => {
    e.preventDefault();
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-form login-page-form-wrapper bulk-id-register-form">
      <LoginFormHeader
        ipAddress={ipAddress}
        actionMessage="Registration ID Redemption"
        infoMessage="Enter your Registration ID and password to continue."
      />
      {isInfoBannerVisible && (
        <Banner className="u-els-margin-top" isVisible={isInfoBannerVisible} onClose={() => setIsInfoBannerVisible(false)} hideCloseLabel>
          Registration IDs link your Reaxys account to an organization, giving you access to its entitled features and content.
        </Banner>
      )}
      <form name="bulkIdRegisterForm">
        <div className="login-form__content">
          {isLoading && <SimpleSpinner id={spinnerId} />}
          <div className="els-text-input">
            <ValidatedTextInput
              ref={bulkIdRef}
              key={`bulk-id-${validationResetKey}`}
              id="bulk-id-register-id"
              name="bulkId"
              className="u-els-margin-top e2e-bulk-id-register-id"
              data-e2e="bulk-id-register-id-input"
              placeholder="Enter registration ID"
              required
              label="Registration ID"
              value={data.bulkId}
              onChange={(e) => changeBulkId(e?.target?.value)}
              onBlur={touchBulkId}
              validator={bulkIdValidator}
              onlyValidateWhenTouched
              messages={{ REQUIRED_ERROR_MESSAGE: bulkIdRequiredMessage }}
            />
          </div>
          <div className="els-text-input">
            <ValidatedTextInput
              ref={bulkPasswordRef}
              key={`bulk-password-${validationResetKey}`}
              id="bulk-id-register-password"
              name="bulkPassword"
              autoComplete="current-password"
              className="u-els-margin-top e2e-bulk-id-register-password"
              data-e2e="bulk-id-register-password-input"
              placeholder="Enter your password"
              required
              label="Password"
              value={data.bulkPassword}
              onChange={(e) => changeBulkPassword(e?.target?.value)}
              onBlur={touchPassword}
              validator={bulkPasswordValidator}
              onlyValidateWhenTouched
              messages={{ REQUIRED_ERROR_MESSAGE: bulkPasswordRequiredMessage }}
              type={showPassword ? 'text' : 'password'}
            />
          </div>
          <div className="u-els-margin-top">
            <Button data-e2e="bulk-id-register-show-password-button" type="link" onClick={togglePassword}>
              Show password?
            </Button>
          </div>
          <div className="login-form__content_actions">
            <div className="login-form__content_actions-button">
              <Button
                type="primary"
                expanded
                disabled={isLoading}
                onClick={submitHandler}
                data-e2e="bulk-id-register-continue-button"
                className="e2e-login-sign-in">
                Continue
              </Button>
            </div>
          </div>
        </div>
      </form>

      <div className="login-form__message login-divider u-els-margin-top-1o2">
        <div className="separator-line" />
        <div className="separator-content u-els-margin-left-1o2">Or continue with:</div>
        <div className="separator-line u-els-margin-left-1o2" />
      </div>
      <div className="login-form__message">
        <ButtonWithIcon
          type="tertiary"
          sprite="Institution"
          name="Institution sign in"
          onClick={() => {
            history.push('/institution');
          }}
          expanded
          data-e2e="institution-sign-in-button"
          className="u-els-margin-top-1x1o2 e2e-login-institution-sign-in">
          Institution sign in
        </ButtonWithIcon>
      </div>

      <span className="login-form__message signup-trouble u-els-margin-top-1x1o2">
        <span>Don&apos;t have an account?</span>
        <LinkElement
          className="u-els-padding-left-1o4"
          data-e2e="bulk-id-register-sign-up-link"
          inline
          href={paths.REGISTER}
          onClick={(e) => {
            e.preventDefault();
            history.push(paths.REGISTER);
          }}>
          Register
        </LinkElement>
      </span>

      <div className="login-form__message u-els-margin-top-1x1o2">
        <LinkElement
          type="link"
          iconSprite="UpRight"
          iconSize="xs"
          iconRight
          aria-label="Privacy Policy"
          data-e2e="bulk-id-register-privacy-policy-link"
          // eslint-disable-next-line sonarjs/no-clear-text-protocols
          href="http://www.elsevier.com/privacypolicy?dgcid=RN_AGCM_Sourced_300005028"
          target="_blank"
          rel="noreferrer">
          Privacy Policy
        </LinkElement>

        <LinkElement
          type="link"
          iconSprite="UpRight"
          iconSize="xs"
          iconRight
          aria-label="Terms and conditions"
          data-e2e="bulk-id-register-terms-and-conditions-link"
          className="u-els-margin-left"
          // eslint-disable-next-line sonarjs/no-clear-text-protocols
          href="http://www.elsevier.com/termsconditions?dgcid=RN_AGCM_Sourced_300005028"
          target="_blank"
          rel="noreferrer">
          Terms and conditions
        </LinkElement>
      </div>
    </div>
  );
}

BulkIdRegisterForm.propTypes = {
  data: PropTypes.shape({ bulkId: PropTypes.string, bulkPassword: PropTypes.string }),
  setData: PropTypes.func,
  ipAddress: PropTypes.string,
  loginFail: PropTypes.object,
  isLoading: PropTypes.bool,
  submit: PropTypes.func.isRequired,
  clearLoginFail: PropTypes.func.isRequired,
};

export default BulkIdRegisterForm;
